import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';

import { colors } from 'src/theme/colors';
import { zIndex } from 'src/theme/zIndex';
import { LINKS } from 'src/utils/constants';
import {
  acceptAllConsent,
  rejectAllConsent,
  saveConsent,
  useConsent,
} from 'src/utils/consent';

/*
 * A slim bar pinned to the bottom edge. It is not a modal and does not trap focus or
 * block the page, and being position:fixed it cannot shift layout. Accept and reject
 * share one style: rejecting must be as easy as accepting.
 */
const Bar = styled.div`
  background-color: ${colors.grey100};
  border-top: 1px solid ${colors.grey400};
  bottom: 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  color: ${colors.black};
  font-size: 12px;
  left: 0;
  line-height: 1.4;
  padding: 8px 16px;
  position: fixed;
  right: 0;
  z-index: ${zIndex.consent};
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1200px;
`;

const Text = styled.p`
  flex: 1 1 320px;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
`;

const Button = styled.button`
  background: transparent;
  border: 1px solid ${colors.black};
  border-radius: 4px;
  color: ${colors.black};
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  min-height: 32px;
  padding: 4px 14px;

  &:hover,
  &:focus-visible {
    background-color: ${colors.black};
    color: ${colors.white};
  }
`;

const LinkButton = styled(Button)`
  border-color: transparent;
  font-weight: 400;
  text-decoration: underline;
`;

const Options = styled.fieldset`
  border: 0;
  display: grid;
  gap: 4px;
  margin: 8px auto 0;
  max-width: 1200px;
  padding: 0;
`;

const Option = styled.label`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

/** Mounted only while open so its checkbox always starts from the stored choice. */
const Settings: FC = () => {
  const current = useConsent();
  const [analytics, setAnalytics] = useState(current.analytics);

  return (
    <Options>
      <Option>
        <input type="checkbox" checked disabled readOnly /> Būtini
      </Option>
      <Option>
        <input
          type="checkbox"
          checked={analytics}
          onChange={() => setAnalytics(value => !value)}
        />{' '}
        Rinkodara
      </Option>
      <div>
        <Button type="button" onClick={() => saveConsent({ analytics })}>
          Išsaugoti pasirinkimą
        </Button>
      </div>
    </Options>
  );
};

const CookieBanner: FC = () => {
  const { ready, bannerOpen } = useConsent();
  const [expanded, setExpanded] = useState(false);

  if (!ready || !bannerOpen) return null;

  return (
    <Bar role="region" aria-label="Slapukų nustatymai">
      <Row>
        <Text>
          Būtini slapukai veikia visada. Su jūsų sutikimu naudojame ir
          analitikos bei rinkodaros įrankius.{' '}
          <Link to={LINKS.COOKIES}>Slapukų politika</Link>
        </Text>
        <Actions>
          <LinkButton
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
          >
            Nustatymai
          </LinkButton>
          <Button type="button" onClick={rejectAllConsent}>
            Atmesti
          </Button>
          <Button type="button" onClick={acceptAllConsent}>
            Sutikti
          </Button>
        </Actions>
      </Row>
      {expanded && <Settings />}
    </Bar>
  );
};

export default CookieBanner;
